/**
 * Fait : copie un texte dans le presse-papier, avec un REPLI quand le chemin
 *   moderne refuse — et rend ce qui s'est passé, pour que l'écran puisse
 *   l'ANNONCER. C'est le critère 150.
 *
 *   POURQUOI UN REPLI, ET PAS SEULEMENT `navigator.clipboard`. Ce chemin-là
 *   exige un contexte sécurisé et une permission ; il n'existe pas du tout
 *   hors HTTPS, et il refuse dans plusieurs situations ordinaires (onglet qui
 *   n'a pas le focus, réglage du navigateur). Le repli — une zone de saisie
 *   invisible, sélectionnée, et l'ancienne commande d'édition — marche là où
 *   l'autre renonce.
 *
 *   POURQUOI ÇA REND UN RÉSULTAT AU LIEU DE SE TAIRE. Une copie est un geste
 *   dont l'effet se produit hors de la page : rien ne bouge à l'écran. Sans
 *   une phrase qui dit « c'est copié », un bouton qui a marché est
 *   indiscernable d'un bouton mort — et pour quelqu'un qui voit mal, c'est le
 *   seul retour possible. C'est la même raison qui a fait annoncer le
 *   téléchargement au lot 2.
 *
 * Ne fait pas : il n'affiche rien lui-même, ne touche à aucun style, et ne
 *   ment jamais — si les deux chemins échouent, il le DIT, au lieu de laisser
 *   croire à une réussite.
 */

/** Ce qu'une tentative de copie a donné. */
export type IssueDeCopie = 'copie' | 'refuse';

/**
 * Les deux chemins, DONNÉS plutôt que pris dans la page.
 *
 * POURQUOI CETTE COUTURE EXISTE — elle n'est pas de l'élégance, elle est ce
 * qui rend le critère 150 mesurable. Le plan exige que le repli soit éprouvé
 * par un test unitaire avec un presse-papier qui refuse ; or les tests
 * unitaires du dépôt tournent SANS DOM (mesuré le 2026-08-25 : `document`
 * n'existe pas), et ajouter un environnement de navigateur serait une
 * dépendance nouvelle, que le plan met hors périmètre.
 *
 * Le test fournit donc deux faux chemins et vérifie l'ENCHAÎNEMENT — refus du
 * moderne, passage à l'ancien — qui est précisément ce que le critère nomme.
 * Le contenu du repli, lui, se prouve dans un vrai navigateur : c'est là
 * qu'un `<textarea>` existe.
 */
export interface CheminsDeCopie {
  /** Le chemin moderne. Absent quand le navigateur ne l'offre pas. */
  readonly moderne?: ((texte: string) => Promise<void>) | undefined;

  /** L'ancien chemin, qui rend vrai s'il a copié. */
  readonly ancien: (texte: string) => boolean;
}

/**
 * Le repli : une zone de saisie hors de l'œil, sélectionnée, et l'ancienne
 * commande d'édition du navigateur.
 *
 * Elle est posée hors écran plutôt que cachée : un élément vraiment caché
 * (`display: none`, `hidden`) n'est pas sélectionnable, et la commande
 * n'aurait rien à copier.
 */
function copieParLAncienChemin(texte: string): IssueDeCopie {
  const zone = document.createElement('textarea');

  zone.value = texte;
  zone.setAttribute('readonly', '');
  zone.setAttribute('aria-hidden', 'true');
  zone.style.position = 'fixed';
  zone.style.top = '-1000px';
  zone.style.opacity = '0';

  document.body.appendChild(zone);

  try {
    zone.select();
    zone.setSelectionRange(0, texte.length);

    // `execCommand` est déclaré obsolète, et reste le SEUL chemin quand
    // `navigator.clipboard` n'existe pas ou refuse. Obsolète n'est pas absent.
    const reussi = document.execCommand('copy');

    return reussi ? 'copie' : 'refuse';
  } catch {
    return 'refuse';
  } finally {
    // Dans tous les cas la zone repart : un `<textarea>` oublié dans la page
    // s'accumulerait à chaque clic et resterait dans l'ordre de tabulation.
    zone.remove();
  }
}

/**
 * Copie ce texte, par le chemin moderne s'il veut bien, par l'ancien sinon.
 *
 * L'ordre compte : le chemin moderne d'abord, parce qu'il est le seul à
 * fonctionner quand la page n'a pas le droit d'exécuter une commande
 * d'édition ; l'ancien ensuite, parce qu'il est le seul à fonctionner hors
 * contexte sécurisé.
 */
export async function copieDansLePressePapier(
  texte: string,
  chemins: CheminsDeCopie = cheminsDuNavigateur(),
): Promise<IssueDeCopie> {
  const { moderne, ancien } = chemins;

  if (moderne !== undefined) {
    try {
      await moderne(texte);

      return 'copie';
    } catch {
      // Refus du presse-papier moderne : on ne rend pas la main tout de suite,
      // l'ancien chemin marche encore dans ce cas précis.
    }
  }

  return ancien(texte) ? 'copie' : 'refuse';
}

/** Les vrais chemins, tels que la page les offre au moment du clic. */
function cheminsDuNavigateur(): CheminsDeCopie {
  const presse = navigator.clipboard as Clipboard | undefined;

  return {
    moderne:
      presse !== undefined && typeof presse.writeText === 'function'
        ? (texte) => presse.writeText(texte)
        : undefined,
    ancien: (texte) => copieParLAncienChemin(texte) === 'copie',
  };
}
